import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import FloatingBubbles from '../components/FloatingBubbles';
import Avatar from '../components/Avatar';
import axios from 'axios';

const PROJECT_TYPES = {
  'créer': { icon: '🔧', color: '#2B6CB0', bg: '#EBF4FF' },
  'analyser': { icon: '📊', color: '#276749', bg: '#F0FFF4' },
  'médiatiser': { icon: '📡', color: '#9B2C2C', bg: '#FFF5F5' },
  'jouer': { icon: '🎮', color: '#6B46C1', bg: '#FAF5FF' },
};

const POST_TYPE_COLORS = {
  analyse: { bg: '#EBF4FF', color: '#2B6CB0' },
  projet: { bg: '#F0FFF4', color: '#276749' },
  decouverte: { bg: '#FFF5F5', color: '#9B2C2C' },
  post: { bg: '#F7F9FC', color: '#4A5568' }
};

export default function Search() {
  const { API } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ users: [], posts: [], projects: [] });
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  const handleSearch = useCallback(async (q) => {
    if (q.length < 2) { setResults({ users: [], posts: [], projects: [] }); setSearched(false); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/search?q=${encodeURIComponent(q)}`);
      setResults(res.data);
      setSearched(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [API]);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    handleSearch(val);
  };

  const totalResults = results.users.length + results.posts.length + results.projects.length;

  const tabs = [
    { key: 'all', label: 'Tout', count: totalResults },
    { key: 'users', label: 'Membres', count: results.users.length },
    { key: 'posts', label: 'Posts', count: results.posts.length },
    { key: 'projects', label: 'Projets', count: results.projects.length },
  ];

  return (
    <>
      <FloatingBubbles />

      <div style={{ position: 'relative', zIndex: 1, minHeight: '100vh' }}>

        {/* Hero */}
        <div style={{
          background: 'rgba(235,244,255,0.85)',
          backdropFilter: 'blur(4px)',
          padding: '48px 20px 0',
          textAlign: 'center',
          borderBottom: '1px solid rgba(190,227,248,0.5)'
        }}>
          <div className="fade-up" style={{ marginBottom: '12px' }}>
            <span style={{
              background: 'rgba(43,108,176,0.1)', color: 'var(--blue)',
              fontSize: '11px', fontWeight: 600, padding: '5px 16px',
              borderRadius: '20px', letterSpacing: '1px'
            }}>RECHERCHE</span>
          </div>
          <h1 className="fade-up" style={{
            fontSize: '32px', fontWeight: 800, color: 'var(--dark)',
            letterSpacing: '-1px', marginBottom: '20px'
          }}>
            Explore Agora
          </h1>

          {/* Search bar */}
          <div className="fade-up" style={{ maxWidth: '520px', margin: '0 auto 24px', position: 'relative' }}>
            <input
              type="text"
              value={query}
              onChange={handleChange}
              placeholder="Membres, posts, projets..."
              autoFocus
              style={{
                width: '100%', padding: '16px 24px 16px 52px',
                border: '1.5px solid var(--blue-border)',
                borderRadius: '32px', fontSize: '15px', outline: 'none',
                background: 'rgba(255,255,255,0.92)',
                boxShadow: '0 4px 20px rgba(43,108,176,0.1)',
                transition: 'all .2s'
              }}
              onFocus={e => { e.target.style.borderColor = 'var(--blue)'; e.target.style.boxShadow = '0 4px 24px rgba(43,108,176,0.2)'; }}
              onBlur={e => { e.target.style.borderColor = 'var(--blue-border)'; e.target.style.boxShadow = '0 4px 20px rgba(43,108,176,0.1)'; }}
            />
            <span style={{
              position: 'absolute', left: '20px', top: '50%',
              transform: 'translateY(-50%)', fontSize: '18px', color: 'var(--muted)'
            }}>🔍</span>
            {loading && (
              <span style={{
                position: 'absolute', right: '20px', top: '50%',
                transform: 'translateY(-50%)', fontSize: '13px', color: 'var(--muted)'
              }}>...</span>
            )}
          </div>

          {/* Tabs */}
          {searched && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '0' }}>
              {tabs.map(tab => (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: '12px 20px', fontSize: '13px', fontWeight: 600,
                  color: activeTab === tab.key ? 'var(--blue)' : 'var(--muted)',
                  borderBottom: `2px solid ${activeTab === tab.key ? 'var(--blue)' : 'transparent'}`,
                  transition: 'all .2s', display: 'flex', alignItems: 'center', gap: '6px'
                }}>
                  {tab.label}
                  {tab.count > 0 && (
                    <span style={{
                      background: activeTab === tab.key ? 'var(--blue)' : 'var(--border)',
                      color: activeTab === tab.key ? '#fff' : 'var(--muted)',
                      fontSize: '10px', fontWeight: 700,
                      padding: '1px 6px', borderRadius: '10px'
                    }}>{tab.count}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Results */}
        <div style={{ maxWidth: '680px', margin: '0 auto', padding: '32px 20px 48px' }}>

          {!searched && !query && (
            <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--muted)' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
              <p style={{ fontSize: '16px', fontWeight: 500, color: 'var(--dark)' }}>Recherche sur Agora</p>
              <p style={{ fontSize: '14px', marginTop: '8px' }}>Membres, posts, projets — tout est là.</p>
            </div>
          )}

          {searched && totalResults === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--muted)' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🤷</div>
              <p style={{ fontSize: '16px', fontWeight: 500 }}>Aucun résultat pour "{query}"</p>
            </div>
          )}

          {searched && totalResults > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

              {/* Users */}
              {(activeTab === 'all' || activeTab === 'users') && results.users.length > 0 && (
                <div>
                  {activeTab === 'all' && (
                    <h3 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--muted)', letterSpacing: '1px', marginBottom: '12px' }}>
                      MEMBRES
                    </h3>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {results.users.map((u, i) => (
                      <Link key={u.id} to={`/profile/${u.username}`} style={{ textDecoration: 'none' }}>
                        <div style={{
                          background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(12px)',
                          borderRadius: '16px', padding: '16px 20px',
                          border: '1px solid var(--border)', boxShadow: 'var(--shadow)',
                          display: 'flex', alignItems: 'center', gap: '14px',
                          transition: 'all .25s',
                          animation: `fadeUp 0.4s ease ${i * 0.05}s both`
                        }}
                          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.borderColor = 'var(--blue-border)'; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                        >
                          <Avatar user={u} size={46} fontSize={15} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--dark)' }}>{u.username}</div>
                            {u.bio && <div style={{ fontSize: '13px', color: 'var(--muted)', marginTop: '2px' }}>{u.bio.slice(0, 60)}{u.bio.length > 60 ? '...' : ''}</div>}
                          </div>
                          <span style={{ fontSize: '12px', color: 'var(--blue)', fontWeight: 600 }}>Voir →</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Posts */}
              {(activeTab === 'all' || activeTab === 'posts') && results.posts.length > 0 && (
                <div>
                  {activeTab === 'all' && (
                    <h3 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--muted)', letterSpacing: '1px', marginBottom: '12px' }}>
                      POSTS
                    </h3>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {results.posts.map((post, i) => {
                      const type = POST_TYPE_COLORS[post.post_type] || POST_TYPE_COLORS.post;
                      return (
                        <div key={post.id} style={{
                          background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(12px)',
                          borderRadius: '16px', padding: '18px 20px',
                          border: '1px solid var(--border)', boxShadow: 'var(--shadow)',
                          animation: `fadeUp 0.4s ease ${i * 0.05}s both`
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                            <Link to={`/profile/${post.author.username}`}>
                              <Avatar user={post.author} size={32} fontSize={11} />
                            </Link>
                            <Link to={`/profile/${post.author.username}`} style={{ fontSize: '13px', fontWeight: 600, color: 'var(--dark)' }}>
                              {post.author.username}
                            </Link>
                            <span style={{ background: type.bg, color: type.color, fontSize: '11px', padding: '2px 8px', borderRadius: '10px', fontWeight: 600, marginLeft: 'auto' }}>
                              {post.post_type}
                            </span>
                          </div>
                          <p style={{ fontSize: '14px', color: '#2D3748', lineHeight: 1.7, margin: 0, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {post.content}
                          </p>
                          <div style={{ display: 'flex', gap: '16px', marginTop: '10px' }}>
                            <span style={{ fontSize: '12px', color: 'var(--muted)' }}>♡ {post.likes_count}</span>
                            <span style={{ fontSize: '12px', color: 'var(--muted)' }}>💬 {post.comments_count}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Projects */}
              {(activeTab === 'all' || activeTab === 'projects') && results.projects.length > 0 && (
                <div>
                  {activeTab === 'all' && (
                    <h3 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--muted)', letterSpacing: '1px', marginBottom: '12px' }}>
                      PROJETS
                    </h3>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {results.projects.map((project, i) => {
                      const tInfo = PROJECT_TYPES[project.project_type] || PROJECT_TYPES['créer'];
                      return (
                        <Link key={project.id} to={`/projects/${project.id}`} style={{ textDecoration: 'none' }}>
                          <div style={{
                            background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(12px)',
                            borderRadius: '16px', padding: '18px 20px',
                            border: '1px solid var(--border)', boxShadow: 'var(--shadow)',
                            borderLeft: `4px solid ${project.color}`,
                            transition: 'all .25s',
                            animation: `fadeUp 0.4s ease ${i * 0.05}s both`
                          }}
                            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = 'var(--shadow-hover)'; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'var(--shadow)'; }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                              <span style={{ background: tInfo.bg, color: tInfo.color, fontSize: '11px', padding: '3px 10px', borderRadius: '20px', fontWeight: 600 }}>
                                {tInfo.icon} {project.project_type}
                              </span>
                              <span style={{ fontSize: '12px', color: 'var(--muted)', marginLeft: 'auto' }}>
                                {project.members_count} membre{project.members_count > 1 ? 's' : ''}
                              </span>
                            </div>
                            <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--dark)', marginBottom: '4px' }}>{project.title}</div>
                            <div style={{ fontSize: '13px', color: 'var(--text)', lineHeight: 1.6, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {project.description}
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
